/**
 * App installation.
 *
 * Chrome fires `beforeinstallprompt` once, early — often before React has
 * mounted. If nobody calls `preventDefault()` on it in time the event is gone
 * and the deferred prompt can never be shown. So the listener is registered at
 * module scope, and this module is imported from main.tsx before anything
 * renders.
 *
 * Safari fires nothing at all: on iOS the only route is the user tapping
 * Share -> Add to Home Screen themselves, which is why the onboarding flow
 * leans on instructions there rather than a button.
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chrome's own mini-infobar so the prompt appears where our
    // onboarding asks for it, in context, instead of as a stray banner.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

/** Whether we can show a real, one-tap install prompt right now. */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Subscribe to install-availability changes. Returns an unsubscribe function. */
export function onInstallAvailabilityChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Show the browser's install prompt. Must be called from a user gesture.
 * Returns the outcome, or 'unavailable' when there is no deferred prompt —
 * which is the normal case on iOS.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';

  const event = deferredPrompt;
  // The event can only be used once, whatever the outcome.
  deferredPrompt = null;
  notify();

  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome;
  } catch {
    return 'dismissed';
  }
}
